import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Sparkline } from './Sparkline';
import { EASING, STAGGER_DELAY } from './chart-tokens';
import { cn } from '../ui/utils';

/**
 * Card moderno de KPI com:
 * - Ícone com background gradient
 * - Trend indicator (↑/↓ %) com cor verde/vermelho
 * - Sparkline embutido pra mostrar histórico
 * - Mount com fade-up + stagger via prop `index`
 * - Hover com lift sutil + sombra mais profunda
 */
export function KpiCard({
  index = 0,
  icon: Icon,
  iconGradient = 'from-blue-500 to-blue-600',
  title,
  value,
  trendPercent = null, // number ou null
  trendUpIsBad = false, // pra "Gastos do mês" — subir é ruim
  warning = false, // override pra estado vermelho
  warningLabel,
  sparklineData = null, // [{value: number}, ...] opcional
  sparklineColor,
  subtitle, // fallback se nao tem trend
}) {
  const isUp = (trendPercent ?? 0) > 0;
  const isDown = (trendPercent ?? 0) < 0;
  const trendIsPositive = trendUpIsBad ? isDown : isUp;
  const trendColor = warning
    ? 'text-red-600'
    : trendPercent === null
      ? 'text-slate-500'
      : trendIsPositive
        ? 'text-emerald-600'
        : 'text-rose-600';
  const TrendIcon = isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.5,
        delay: index * STAGGER_DELAY,
        ease: EASING.smoothOut,
      }}
      whileHover={{
        y: -4,
        transition: { duration: 0.2, ease: EASING.natural },
      }}
    >
      <Card
        className={cn(
          'group relative overflow-hidden border-slate-200/60 transition-shadow',
          'hover:shadow-xl hover:shadow-slate-900/5',
          warning && 'border-red-300 bg-gradient-to-br from-red-50 to-white'
        )}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br shadow-sm',
                  iconGradient
                )}
              >
                <Icon className="h-5 w-5 text-white" />
              </div>
              <p className="text-sm font-medium text-slate-500 truncate">{title}</p>
            </div>
          </div>

          <div className="mt-4">
            <p
              className={cn(
                'text-2xl font-bold tracking-tight',
                warning ? 'text-red-700' : 'text-slate-900'
              )}
            >
              {value}
            </p>

            <div className="mt-2 flex items-center justify-between gap-2">
              {warning ? (
                <div className="flex items-center gap-1 text-xs font-medium text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>{warningLabel}</span>
                </div>
              ) : trendPercent !== null ? (
                <div className={cn('flex items-center gap-1 text-xs font-medium', trendColor)}>
                  <TrendIcon className="h-3.5 w-3.5" />
                  <span>
                    {isUp ? '+' : ''}
                    {trendPercent.toFixed(1)}%
                  </span>
                </div>
              ) : subtitle ? (
                <span className="text-xs text-slate-500">{subtitle}</span>
              ) : (
                <span />
              )}

              {sparklineData && sparklineData.length > 1 && (
                <div className="w-20 shrink-0">
                  <Sparkline data={sparklineData} color={sparklineColor} height={32} />
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default KpiCard;
